import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Divider, Dropdown, Input, message, Modal, Space, Table, Tooltip, Typography } from 'antd';
import { getInvitationListApi, deleteInvitationApi } from '@services/Invitation/Invitation';
import { getInvitationGroupOneApi } from '@services/Invitation/InvitationGroup';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import copy from 'copy-to-clipboard';
import config from '@config';
import { convertDateRangeToLocalText } from '@utils/utils';
import { invitationActions } from './store/invitationSlice';
import PageHeader from '@components/PageHeader/PageHeader';
import './InvitationList.less';
import { TABLE_PAGE_SIZE_OPTIONS } from '@/utils/property';

const { Search } = Input;

const { Text } = Typography;

const { setInvitationFilter: setFilter, setInvitationCursor: setCursor } = invitationActions;

export default function InvitationList() {
    const navigate = useNavigate();

    const params = useParams();
    const { gid } = params;

    const dispatch = useDispatch();
    const filter = useSelector(state => state.invitation.invitation.filter);
    const cursor = useSelector(state => state.invitation.invitation.cursor);

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const [total, setTotal] = useState(0);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [invitationGroupOne, setInvitationGroupOne] = useState('');

    const onChangePage = (page, pageSize) => {
        setTableData([]);
        dispatch(
            setFilter({
                ...filter,
                page,
                pageSize,
            }),
        );
    };
    const onShowSizeChange = (page, pageSize) => onChangePage(1, pageSize);

    const getInvitations = async () => {
        try {
            setLoading(true);
            const params = {
                ...filter,
                groupId: gid,
                limit: filter.pageSize,
            };
            const res = await getInvitationListApi(params);
            if (res.status_code === 200) {
                setTableData(res.data.list);
                setTotal(res.data.totalCount);
            }
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getInvitations();
    }, [filter]);

    useEffect(() => {
        getInvitationGroupOneApi({ id: gid }).then(res => {
            if (res.status_code === 200) {
                setInvitationGroupOne(res.data.name);
            }
        });
    }, []);

    const onSearchName = value => {
        dispatch(
            setFilter({
                ...filter,
                name: value || undefined,
                page: 1,
            }),
        );
    };

    const preview = item => {
        if (item.invitationGroup.applicationType === 1) {
            window.open(config.INVITATION_PREFIX + '/c/' + item.code, '_blank');
        } else if (item.invitationGroup.applicationType === 2) {
            window.open(config.INVITATION_PREFIX + '/s/' + item.code, '_blank');
        }
    };

    const copyInvitation = item => {
        if (item.invitationGroup.applicationType === 1) {
            copy(config.INVITATION_PREFIX + '/c/' + item.code);
            message.success('复制成功');
        } else if (item.invitationGroup.applicationType === 2) {
            copy(config.INVITATION_PREFIX + '/s/' + item.code);
            message.success('复制成功');
        }
    };

    const onDeleteInvitation = id => {
        Modal.confirm({
            title: '提示',
            content: '确定删除当前邀请函？',
            okText: '确认',
            cancelText: '取消',
            onOk: () => {
                deleteInvitationApi({ id }).then(res => {
                    if (res.status_code === 200) {
                        message.success(res.message);
                    }

                    getInvitations();
                });
            },
        });
    };

    const columns = [
        {
            title: '#',
            dataIndex: 'id',
            width: 60,
            render: (_, record) => <Tooltip title={`邀请函编码：${record.code}`}>{record.id}</Tooltip>,
        },
        {
            title: '邀请函名称',
            dataIndex: 'name',
            render: (_, record) => (
                <>
                    <span>{record.name}</span>
                    {!!record.remarks && (
                        <Text type="secondary">
                            <br />
                            <span style={{ marginLeft: 4 }}>{record.remarks}</span>
                        </Text>
                    )}
                </>
            ),
        },
        {
            title: '类型',
            dataIndex: 'type',
            width: 100,
            render: text => {
                return text === 1 ? '统一邀请码' : '一人一码';
            },
        },
        {
            title: '已反馈/总邀请',
            dataIndex: 'count',
            width: 150,
            render: (text, row, index) => {
                return (
                    <span>
                        <Text type="success">{row.coupleCount}</Text> / {row.totalCount}
                    </span>
                );
            },
        },
        {
            title: '有效期',
            dataIndex: 'validDate',
            width: 200,
            render: (text, row, index) => {
                return <>{convertDateRangeToLocalText(row.startTime, row.endTime)}</>;
            },
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 300,
            fixed: 'right',
            render: (_, record) => {
                const items =
                    record?.type === '1'
                        ? [
                              { label: '预览邀请函', key: 'view', onClick: () => preview(record) },
                              { label: '复制邀请函链接', key: 'link', onClick: () => copyInvitation(record) },
                              {
                                  label: '删除邀请函',
                                  key: 'delete',
                                  onClick: () => onDeleteInvitation(record.id),
                                  danger: true,
                              },
                          ]
                        : [
                              {
                                  label: '删除邀请函',
                                  key: 'delete',
                                  onClick: () => onDeleteInvitation(record.id),
                                  danger: true,
                              },
                          ];

                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        <Button
                            size="small"
                            type="link"
                            onClick={() => {
                                dispatch(setCursor(record.id));
                                navigate(`/invitation/g/${gid}/invitations/${record.id}/users`);
                            }}
                        >
                            管理邀请用户
                        </Button>
                        <Button
                            size="small"
                            type="link"
                            onClick={() => {
                                dispatch(setCursor(record.id));
                                navigate(`/invitation/g/${gid}/invitations/${record.id}`);
                            }}
                        >
                            编辑邀请函
                        </Button>
                        <Dropdown menu={{ items }} placement="bottomRight">
                            <Button size="small" type="link" className="more-operation">
                                <i className="bi bi-three-dots"></i>
                            </Button>
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    const onRefresh = () => {
        void getInvitations();
    };

    useEffect(() => {
        document.title = '邀请函管理-医邀请';
    }, []);

    return (
        <div className="page-container invitation-list-page">
            <PageHeader
                title={
                    <>
                        <i className="bi bi-chevron-left page-header-back-btn" onClick={() => navigate('/invitation/groups')} />
                        <span>邀请函管理</span>
                    </>
                }
                breadcrumb={[{ label: '医邀请', path: '/invitation' }, { label: '邀请组管理', path: '/invitation/groups' }, { label: invitationGroupOne || '邀请函管理' }]}
            />

            <div ref={tableRegionRef} className="invitation-list-table-region">
                <div className="main-toolbar invitation-list-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="primary" onClick={() => navigate(`/invitation/g/${gid}/invitations/new`)}>
                                新增邀请函
                            </Button>
                            <Search placeholder="邀请函名称关键字" allowClear style={{ width: 250 }} onSearch={onSearchName} />
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={onRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="invitation-list-table-wrap invitation-list">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        bordered
                        size="medium"
                        columns={columns}
                        dataSource={tableData}
                        loading={loading}
                        rowKey={item => item.id}
                        rowClassName={row => (row.id === cursor ? 'store-cursor' : '')}
                        scroll={{
                            x: 'max-content',
                            ...(tableScrollY != null ? { y: tableScrollY } : {}),
                        }}
                        sticky
                        pagination={{
                            total,
                            showTotal: () => `共${total}条`,
                            current: filter.page,
                            pageSize: filter.pageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                            onChange: onChangePage,
                            onShowSizeChange,
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
